'use client'

import {
  isBlockquote,
  isCode,
  isHeading,
  isLink,
  isList,
  isListItem,
  isParagraph,
  isThematicBreak,
} from 'datocms-structured-text-utils'
import {useCallback, useMemo, useState} from 'react'
import {
  StructuredText,
  renderMarkRule,
  renderNodeRule,
} from 'react-datocms/structured-text'
import {type CustomAssetSlide} from 'yet-another-react-lightbox'
import {
  handleBoldMark,
  handleCodeMark,
  handleIOMark,
  handleSuperscriptMark,
} from '~/cms/components/WYSIWYG/_utils/markRules'
import {
  formatHeadingLink,
  handleHeadingNode,
  handleLinkNode,
  handleListItemNode,
  handleListNode,
  handleParagraphNode,
} from '~/cms/components/WYSIWYG/_utils/nodeRules'
import {getMediaAssetProps} from '~/cms/helpers/getMediaAssetProps'
import {type MediaAssetFragment} from '~/cms/helpers/getMediaAssetProps/_generated/fragment.graphql'
import {
  ARTICLE_WYSIWYG_TYPE,
  HgArticleWYSIWYGContainer,
} from '~/design-system/hg/components/HgWYSIWYG'
import {safeUnreachableCase} from '~/utils/unreachableCase'
import Asset from '../../Asset'
import {DatoConstant} from '../../Constants/DatoConstant'
import DisclaimerLink from '../../SingleLineStructuredText/DisclaimerLink'
import {DatoWYSIWYGEmbed} from '../../WYSIWYG/_components/DatoWYSIWYGEmbed'
import {DatoWYSIWYGMediaAsset} from '../../WYSIWYG/_components/DatoWYSIWYGMediaAsset'
import {DatoWYSIWYGMediaGrid} from '../../WYSIWYG/_components/DatoWYSIWYGMediaGrid'
import {DatoWYSIWYGPullQuote} from '../../WYSIWYG/_components/DatoWYSIWYGPullQuote'
import {DatoWYSIWYGStatistic} from '../../WYSIWYG/_components/DatoWYSIWYGStatistic'
import {DatoWYSIWYGTable} from '../../WYSIWYG/_components/DatoWYSIWYGTable'
import {DatoWYSIWYGSplitAssetAndBody} from './_components/DatoSplitAssetAndBody'
import {DatoWYSIWYGAside} from './_components/DatoWYSIWYGAside'
import {DatoWYSIWYGLightbox} from './_components/DatoWYSIWYGLightbox'
import {DatoWYSIWYGNewsletterPromo} from './_components/DatoWYSIWYGPromoNewsletter'
import {DatoWYSIWYGProductPromo} from './_components/DatoWYSIWYGPromoProduct'
import {DatoWYSIWYGPromoRecirculation} from './_components/DatoWYSIWYGPromoRecirculation'
import {type ArticleWysiwygFragment} from './_generated/fragment.graphql'
import {handleDropCapMark} from './_utils/markRules'
import {
  handleArticleThematicBreak,
  handleBlockQuoteNode,
  handleCodeBlockNode,
} from './_utils/nodeRules'
import {DatoRecordLink} from '~/cms/components/WYSIWYG/_components/DatoRecordLink'

export type ArticleWYSIWYGProps = {
  data?: ArticleWysiwygFragment | null
}

export const ArticleWYSIWYG = ({data}: ArticleWYSIWYGProps) => {
  const [lightboxSlideIndex, setLightboxSlideIndex] = useState(-1)

  const assetComponents = useMemo(() => {
    return data?.article.blocks
      .flatMap(block => {
        switch (block.__typename) {
          case 'WysiwygMediaAssetComponentRecord':
            return block.assetComponent
          case 'ArticleWysiwygSplitAssetAndBodyComponentRecord':
            return block.asset
          case 'WysiwygMediaGridComponentRecord':
            return block.assets
          default:
            return null
        }
      })
      .filter(Boolean) as MediaAssetFragment[]
  }, [data?.article.blocks])

  const slides: CustomAssetSlide[] = useMemo(() => {
    return assetComponents.map(assetComponent => {
      const {assetId, credit, caption, aspectRatio} =
        getMediaAssetProps(assetComponent)
      return {
        slideId: assetId,
        type: 'custom-asset',
        credit,
        caption,
        aspectRatio,
        asset: (
          <Asset
            datoAnimationProps={{
              autoplayVisibilityThreshold: 0.1,
              classNames: {
                video: 'h-full max-h-full max-w-full w-[auto]',
              },
              preRenderVideo: true,
            }}
            datoImageProps={{
              pictureClassName:
                'col-start-1 col-end-auto row-start-1 row-end-auto h-full min-h-0',
              imgStyle: {
                width: 'auto',
                maxWidth: '100%',
                height: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                backgroundSize: 'contain',
              },
            }}
            data={assetComponent.asset}
            aspectRatio={aspectRatio}
          />
        ),
      }
    })
  }, [assetComponents])

  const closeLightbox = useCallback(() => {
    setLightboxSlideIndex(-1)
  }, [setLightboxSlideIndex])

  const openLightbox = useCallback(
    (assetId: string) => {
      const slideIndex = slides.findIndex(slide => slide.slideId === assetId)
      setLightboxSlideIndex(slideIndex)
    },
    [slides]
  )

  if (!data) return null

  return (
    <>
      <DatoWYSIWYGLightbox
        index={lightboxSlideIndex}
        isLightboxOpen={lightboxSlideIndex >= 0}
        slides={slides}
        closeLightbox={closeLightbox}
      />
      <HgArticleWYSIWYGContainer>
        <StructuredText
          customMarkRules={[
            renderMarkRule('code', handleCodeMark),
            renderMarkRule('drop-cap', handleDropCapMark),
            renderMarkRule('io-font', handleIOMark),
            renderMarkRule('strong', handleBoldMark),
            renderMarkRule('superscript', handleSuperscriptMark),
          ]}
          customNodeRules={[
            renderNodeRule(isBlockquote, handleBlockQuoteNode),
            renderNodeRule(isCode, handleCodeBlockNode),
            renderNodeRule(isHeading, ({...props}) => {
              const {id} = formatHeadingLink(props.node)
              return handleHeadingNode({...props, id})
            }),
            renderNodeRule(isLink, handleLinkNode),
            renderNodeRule(isList, nodeProps =>
              handleListNode({...nodeProps, wysiwygType: ARTICLE_WYSIWYG_TYPE})
            ),
            renderNodeRule(isListItem, handleListItemNode),
            renderNodeRule(isParagraph, nodeProps =>
              handleParagraphNode({...nodeProps, wysiwygType: ARTICLE_WYSIWYG_TYPE})
            ),
            renderNodeRule(isThematicBreak, handleArticleThematicBreak),
          ]}
          data={data.article}
          renderBlock={({record}) => {
            switch (record.__typename) {
              case 'ArticleWysiwygAsideComponentRecord':
                return <DatoWYSIWYGAside {...record} />
              case 'WysiwygMediaAssetComponentRecord':
                return (
                  <DatoWYSIWYGMediaAsset {...record} openLightbox={openLightbox} />
                )
              case 'WysiwygMediaGridComponentRecord':
                return (
                  <DatoWYSIWYGMediaGrid {...record} openLightbox={openLightbox} />
                )
              case 'WysiwygEmbedComponentRecord':
                return <DatoWYSIWYGEmbed {...record} />
              case 'WysiwygPullQuoteComponentRecord':
                return <DatoWYSIWYGPullQuote {...record} />
              case 'ArticleWysiwygSplitAssetAndBodyComponentRecord':
                return (
                  <DatoWYSIWYGSplitAssetAndBody
                    {...record}
                    openLightbox={openLightbox}
                    wysiwygType={ARTICLE_WYSIWYG_TYPE}
                  />
                )
              case 'WysiwygStatisticsComponentRecord':
                return <DatoWYSIWYGStatistic statistics={record?.statistics} />
              case 'ArticlePromoNewsletterRecord':
                return <DatoWYSIWYGNewsletterPromo {...record} />
              case 'ArticlePromoProductRecord':
                return <DatoWYSIWYGProductPromo {...record} />
              case 'ArticlePromoRecirculationComponentRecord':
                return <DatoWYSIWYGPromoRecirculation {...record} />
              case 'WysiwygTableComponentRecord':
                return <DatoWYSIWYGTable {...record} />
              default:
                return safeUnreachableCase(record, null)
            }
          }}
          renderLinkToRecord={({record, children: datoChildren}) => {
            switch (record.__typename) {
              case 'ConstantRecord':
                return (
                  <DatoRecordLink datoChildren={datoChildren}>
                    <DatoConstant data={record} />
                  </DatoRecordLink>
                )
              case 'DisclaimerRecord':
                return <DisclaimerLink {...record} text={record.text} />
              default:
                return safeUnreachableCase(record, null)
            }
          }}
        />
      </HgArticleWYSIWYGContainer>
    </>
  )
}
